import { useState } from 'react';
import { Check, Minus, ChevronDown } from 'lucide-react';
import Card from '@/components/ui/Card';
import { roleLabel } from '@/utils/format';
import type { UserRole } from '@/types';

/**
 * Read-only reference matrix of what each role can do. The capability rows below
 * mirror the requireRole() gates enforced on the server routes — keep them in
 * sync when route permissions change. Admin and Group Finance are super users;
 * a User has no entity rights of their own — entering/submitting and
 * agreeing are granted per entity or division through access assignments
 * (Preparer / Reviewer), managed via the key icon on the Users page.
 */

// Column order: broadest access → narrowest.
const ROLES: UserRole[] = ['ADMIN', 'GROUP_FINANCE', 'USER'];

interface Capability {
  label: string;
  detail: string;
  allowed: UserRole[];
}

const CAPABILITIES: Capability[] = [
  {
    label: 'Enter & submit classified lines',
    detail: 'Key CF5xxx+ values for an entity/period and submit for review. Users need a Preparer assignment for the entity (or its division).',
    allowed: ROLES,
  },
  {
    label: 'View entities, statements & validation board',
    detail: 'Users see only the entities covered by their assignments; super users see everything.',
    allowed: ROLES,
  },
  {
    label: 'Agree / revert / reopen cash flows',
    detail: 'Agree a submitted cash flow, send it back to In progress, or reopen an agreed one. Users need a Reviewer assignment for the entity (or its division).',
    allowed: ROLES,
  },
  {
    label: 'View Qlik exchange runs & audit log',
    detail: 'Inspect inbound/outbound exchange history and the audit trail.',
    allowed: ['ADMIN', 'GROUP_FINANCE'],
  },
  {
    label: 'Trigger Qlik sync & edit exchange config',
    detail: 'Run the CSV exchange on demand and change exchange settings.',
    allowed: ['ADMIN'],
  },
  {
    label: 'Manage CF codes & statement layout',
    detail: 'Add/edit CF codes and the consolidated statement layout.',
    allowed: ['ADMIN'],
  },
  {
    label: 'Manage users & entities',
    detail: 'Invite users, change roles, and configure entity access.',
    allowed: ['ADMIN'],
  },
];

export default function RolesMatrix() {
  const [open, setOpen] = useState(true);

  return (
    <Card padding={false}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-6 py-4 text-left"
      >
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            What each role can do
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Reference of the permissions granted by each role.
          </p>
        </div>
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="overflow-x-auto border-t border-gray-200">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-3 text-left font-medium text-gray-700">
                  Capability
                </th>
                {ROLES.map((role) => (
                  <th
                    key={role}
                    className="px-3 py-3 text-center font-medium text-gray-700"
                  >
                    {roleLabel(role)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CAPABILITIES.map((cap) => (
                <tr key={cap.label} className="border-b border-gray-100 last:border-0">
                  <td className="px-6 py-3">
                    <p className="font-medium text-gray-900">{cap.label}</p>
                    <p className="text-xs text-gray-500">{cap.detail}</p>
                  </td>
                  {ROLES.map((role) => {
                    const allowed = cap.allowed.includes(role);
                    return (
                      <td key={role} className="px-3 py-3 text-center">
                        {allowed ? (
                          <Check
                            className="mx-auto h-4 w-4 text-green-600"
                            aria-label="Allowed"
                          />
                        ) : (
                          <Minus
                            className="mx-auto h-4 w-4 text-gray-300"
                            aria-label="Not allowed"
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-6 py-3 text-xs text-gray-500">
            A User with no access assignments cannot see or edit any entity. Grant
            access with the key icon: Preparer to enter and submit, Reviewer to agree,
            on a single entity or a whole division (which covers entities added later).
          </p>
        </div>
      )}
    </Card>
  );
}
